import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { oneKlicks } from '../SDK/oneclick';
import { toast } from 'react-toastify';
import { Badge, Form, InputGroup, Button as BSButton } from 'react-bootstrap';
import { Button, Select } from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EyeOutlined,
    DownloadOutlined,
    MedicineBoxOutlined,
    GlobalOutlined,
    CalendarOutlined,
    ReloadOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const UserMedicalRecords = () => {
    const { userData } = useAuth();
    const [medicalRecords, setMedicalRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [sortBy, setSortBy] = useState('date');
    const [showAddRecord, setShowAddRecord] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    // Form state for adding new records
    const [newRecord, setNewRecord] = useState({
        record_type: 'consultation',
        title: '',
        description: '',
        doctor_name: '',
        hospital_name: '',
        date: '',
        medications: '',
        diagnosis: '',
        treatment: '',
        notes: '',
        attachments: []
    });

    useEffect(() => {
        fetchUserProfile();
    }, [userData?.id]);

    useEffect(() => {
        if (userProfile || userData?.phone) {
            fetchMedicalRecords();
        }
    }, [userProfile, userData?.phone, userData?.id]);

    const fetchUserProfile = async () => {
        if (!userData?.id) return;
        try {
            const tableSchema = '';
            const tableName = 'users';
            const conditions = { id: userData?.id };
            const response = await oneKlicks.read(tableSchema, tableName, conditions, '', '', '', '');

            if (response.success && response.data.length > 0) {
                setUserProfile(response.data[0]);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
        }
    };

    const fetchMedicalRecords = async () => {
        try {
            setLoading(true);
            const tableSchema = '';
            const tableName = 'medical_records';

            const conditions = userData?.id
                ? { user_id: userData.id }
                : { patient_phone: userProfile?.phone || userData?.phone };

            const response = await oneKlicks.read(
                tableSchema,
                tableName,
                conditions,
                'record_date DESC',
                '', '', ''
            );

            if (response.success) {
                setMedicalRecords(response.data || []);
            } else {
                setMedicalRecords([]);
                // createSampleMedicalRecords(); // Optional: Fallback for demo
            }
        } catch (error) {
            console.error('Error fetching medical records:', error);
            // setMedicalRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRecord = async (e) => {
        e.preventDefault();
        try {
            const tableSchema = '';
            const tableName = 'medical_records';
            const data = {
                user_id: userData?.id,
                patient_phone: userProfile?.phone || userData?.phone,
                record_type: newRecord.record_type,
                title: newRecord.title,
                description: newRecord.description,
                doctor_name: newRecord.doctor_name,
                hospital_name: newRecord.hospital_name,
                record_date: newRecord.date,
                medications: newRecord.medications,
                diagnosis: newRecord.diagnosis,
                treatment: newRecord.treatment,
                notes: newRecord.notes,
                attachments: JSON.stringify(newRecord.attachments),
                created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };

            const response = await oneKlicks.create(tableSchema, tableName, data);

            if (response.success) {
                toast.success('Medical record added successfully');
                setShowAddRecord(false);
                resetNewRecord();
                fetchMedicalRecords();
            } else {
                toast.error('Failed to add medical record');
            }
        } catch (error) {
            console.error('Error adding medical record:', error);
            toast.error('Failed to add medical record');
        }
    };

    const resetNewRecord = () => {
        setNewRecord({
            record_type: 'consultation',
            title: '',
            description: '',
            doctor_name: '',
            hospital_name: '',
            date: '',
            medications: '',
            diagnosis: '',
            treatment: '',
            notes: '',
            attachments: []
        });
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        setNewRecord(prev => ({
            ...prev,
            attachments: [...prev.attachments, ...files.map(file => file.name)]
        }));
    };

    const getRecordTypeIcon = (type) => {
        const icons = {
            consultation: '🏠',
            surgery: '⚕️',
            vaccination: '💉',
            emergency: '🚨',
            dental: '🦷',
            lab_test: '🔬',
            prescription: '💊',
            imaging: '📷'
        };
        return icons[type] || '📋';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    const filteredRecords = medicalRecords
        .filter(record =>
            record.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.doctor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.hospital_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .filter(record => filterType === 'all' || record.record_type === filterType)
        .sort((a, b) => {
            switch (sortBy) {
                case 'date':
                    return new Date(b.record_date) - new Date(a.record_date);
                case 'doctor':
                    return (a.doctor_name || '').localeCompare(b.doctor_name || '');
                case 'title':
                    return (a.title || '').localeCompare(b.title || '');
                default:
                    return 0;
            }
        });

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <style>
                {`
                    @media (max-width: 767px) {
                        .dc-table-responsive thead {
                            display: none;
                        }
                        .dc-table-responsive tbody tr {
                            display: block;
                            margin-bottom: 15px;
                            border: 1px solid #eee;
                            border-radius: 8px;
                            background: #fff;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                        }
                        .dc-table-responsive tbody td {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 12px 15px !important;
                            border-bottom: 1px solid #f5f5f5;
                            text-align: right;
                            font-size: 13px;
                            min-height: 45px;
                        }
                        .dc-table-responsive tbody td:last-child {
                            border-bottom: 0;
                        }
                        .dc-table-responsive tbody td::before {
                            content: attr(data-label);
                            font-weight: 700;
                            color: #666;
                            text-transform: uppercase;
                            font-size: 10px;
                            letter-spacing: 0.5px;
                            text-align: left;
                            flex: 1;
                        }
                    }
                `}
            </style>
            <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
                <div className="container-fluid py-3">
                    {/* Header Card matching UserAppointments */}
                    <div className="card mb-4" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <div className="card-body" style={{ padding: '24px 30px' }}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Medical Records</h5>
                                    <Badge bg="info" style={{ borderRadius: '5px', padding: '5px 10px', fontSize: '12px' }}>Healthcare</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <BSButton
                                            variant="outline-primary"
                                            onClick={fetchMedicalRecords}
                                            style={{ height: '40px', borderRadius: "5px", display: 'flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <ReloadOutlined /> Refresh
                                        </BSButton>
                                        <BSButton
                                            className="btn-primary"
                                            style={{
                                                height: '40px',
                                                borderRadius: "5px",
                                                backgroundColor: 'var(--themecolor)',
                                                borderColor: 'var(--themecolor)',
                                                color: '#fff',
                                                fontWeight: '600',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fff';
                                                e.currentTarget.style.color = 'var(--themecolor)';
                                                e.currentTarget.style.borderColor = 'var(--themecolor)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--themecolor)';
                                                e.currentTarget.style.color = '#fff';
                                                e.currentTarget.style.borderColor = 'var(--themecolor)';
                                            }}
                                            onClick={() => setShowAddRecord(true)}
                                        >
                                            <PlusOutlined /> Add New Record
                                        </BSButton>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Search Records
                                    </label>
                                    <InputGroup>
                                        <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none', borderRadius: '5px 0 0 5px' }}>
                                            <SearchOutlined style={{ color: '#666' }} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Search by title, doctor, or clinic..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ height: '38px', borderLeft: 'none', borderRadius: "0 5px 5px 0", fontSize: '14px' }}
                                        />
                                    </InputGroup>
                                </div>
                                <div style={{ minWidth: '160px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Record Type
                                    </label>
                                    <select
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="form-control"
                                        style={{ width: '100%', height: '38px', borderRadius: '5px' }}
                                    >
                                        <option value="all">All Types</option>
                                        <option value="consultation">Consultation</option>
                                        <option value="surgery">Surgery</option>
                                        <option value="vaccination">Vaccination</option>
                                        <option value="emergency">Emergency</option>
                                        <option value="dental">Dental</option>
                                        <option value="lab_test">Lab Test</option>
                                        <option value="prescription">Prescription</option>
                                        <option value="imaging">Imaging</option>
                                    </select>
                                </div>
                                <div style={{ minWidth: '160px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Sort By
                                    </label>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="form-control"
                                        style={{ width: '100%', height: '38px', borderRadius: '5px' }}
                                    >
                                        <option value="date">Date (L-F)</option>
                                        <option value="title">Record Title</option>
                                        <option value="doctor">Doctor Name</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table Card matching UserAppointments */}
                    <div className="card" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Fetching medical history...</p>
                                </div>
                            ) : filteredRecords.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="dc-emptydata-holder">
                                        <div className="dc-emptydata">
                                            <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                                <span></span>
                                                <em className="text-muted">No medical records found</em>
                                            </div>
                                        </div>
                                    </div>
                                    <Button type="primary" onClick={() => setShowAddRecord(true)} style={{ borderRadius: '5px', marginTop: '15px' }}>
                                        Add Record
                                    </Button>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 align-middle dc-table-responsive">
                                        <thead className="bg-light">
                                            <tr style={{ whiteSpace: 'nowrap' }}>
                                                <th data-label="#" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>#</th>
                                                <th data-label="Record Details" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Record Details</th>
                                                <th data-label="Type" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Type</th>
                                                <th data-label="Date" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Date</th>
                                                <th data-label="Clinic / Provider" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666' }}>Clinic / Provider</th>
                                                <th data-label="Actions" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="border-top-0">
                                            {filteredRecords.map((record, index) => (
                                                <tr key={record.id || index}>
                                                    <td data-label="#" style={{ padding: '8px 15px', verticalAlign: 'middle', fontSize: '13px' }}>{index + 1}</td>
                                                    <td data-label="Details" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="d-flex align-items-center gap-2 justify-content-end justify-content-md-start">
                                                            <div style={{ width: '32px', height: '32px', borderRadius: '5px', backgroundColor: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0f2fe' }}>
                                                                <FileTextOutlined style={{ color: '#0ea5e9', fontSize: '16px' }} />
                                                            </div>
                                                            <div className="text-end text-md-start">
                                                                <div style={{ fontWeight: '600', color: '#333', fontSize: '13px', lineHeight: '1.2' }}>{record.title}</div>
                                                                <div style={{ fontSize: '11px', color: '#666', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.diagnosis || record.description || 'Medical Audit Record'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Type" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <Badge bg="light" text="dark" style={{ border: '1px solid #eee', fontWeight: '500', fontSize: '11px', textTransform: 'capitalize' }}>
                                                            {record.record_type}
                                                        </Badge>
                                                    </td>
                                                    <td data-label="Date" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="text-end text-md-start">
                                                            <CalendarOutlined style={{ fontSize: '11px', marginRight: '4px', color: '#999' }} />
                                                            {formatDate(record.record_date)}
                                                        </div>
                                                    </td>
                                                    <td data-label="Provider" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="text-end text-md-start">
                                                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>{record.doctor_name || 'General Practitioner'}</div>
                                                            <div style={{ fontSize: '11px', color: '#999' }}>{record.hospital_name || 'City Clinic'}</div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Actions" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="d-flex gap-2 justify-content-end align-items-center">
                                                            <Button
                                                                size="small"
                                                                className="fit-content btn-info"
                                                                onClick={() => setSelectedRecord(record)}
                                                                style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                icon={<EyeOutlined style={{ fontSize: '14px' }} />}
                                                                title="View Details"
                                                            />
                                                            <Button
                                                                size="small"
                                                                onClick={() => toast.info('Download feature coming soon!')}
                                                                style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                icon={<DownloadOutlined style={{ fontSize: '14px' }} />}
                                                                title="Download PDF"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Record Modal */}
            {showAddRecord && (
                <div className="modal fade dc-uploadimages show" style={{ display: 'block', paddingRight: '17px', background: 'rgba(0,0,0,0.5)' }} aria-modal="true" role="dialog">
                    <div className="dc-modaldialog modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: '800px' }}>
                        <div className="dc-modalcontent modal-content" style={{ borderRadius: '10px', border: 'none' }}>
                            <div className="dc-boxtitle" style={{ borderBottom: '1px solid #f0f0f0', padding: '20px' }}>
                                <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Add New Medical Record<a href="javascript:;" onClick={() => {
                                    setShowAddRecord(false);
                                    resetNewRecord();
                                }} aria-label="Close" style={{ float: 'right' }}><i className="dc-btncancel fa fa-times"></i></a></h2>
                            </div>
                            <div className="dc-modalbody modal-body" style={{ padding: '25px' }}>
                                <form onSubmit={handleAddRecord}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group mb-3">
                                                <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Record Type *</label>
                                                <select
                                                    className="form-control"
                                                    value={newRecord.record_type}
                                                    onChange={(e) => setNewRecord({ ...newRecord, record_type: e.target.value })}
                                                    required
                                                    style={{ height: '42px', borderRadius: '5px' }}
                                                >
                                                    <option value="consultation">Consultation</option>
                                                    <option value="surgery">Surgery</option>
                                                    <option value="vaccination">Vaccination</option>
                                                    <option value="emergency">Emergency</option>
                                                    <option value="dental">Dental</option>
                                                    <option value="lab_test">Lab Test</option>
                                                    <option value="prescription">Prescription</option>
                                                    <option value="imaging">Imaging</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group mb-3">
                                                <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Date *</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={newRecord.date}
                                                    onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                                                    required
                                                    style={{ height: '42px', borderRadius: '5px' }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newRecord.title}
                                            onChange={(e) => setNewRecord({ ...newRecord, title: e.target.value })}
                                            placeholder="e.g., Annual Health Checkup"
                                            required
                                            style={{ height: '42px', borderRadius: '5px' }}
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group mb-3">
                                                <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Doctor Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={newRecord.doctor_name}
                                                    onChange={(e) => setNewRecord({ ...newRecord, doctor_name: e.target.value })}
                                                    placeholder="Dr. Name"
                                                    style={{ height: '42px', borderRadius: '5px' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group mb-3">
                                                <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Hospital/Clinic</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={newRecord.hospital_name}
                                                    onChange={(e) => setNewRecord({ ...newRecord, hospital_name: e.target.value })}
                                                    placeholder="Hospital or clinic name"
                                                    style={{ height: '42px', borderRadius: '5px' }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group mb-3">
                                        <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Diagnosis & Treatment</label>
                                        <textarea
                                            className="form-control"
                                            value={newRecord.diagnosis}
                                            onChange={(e) => setNewRecord({ ...newRecord, diagnosis: e.target.value })}
                                            placeholder="Medical diagnosis and summary..."
                                            rows="3"
                                            style={{ borderRadius: '5px' }}
                                        />
                                    </div>

                                    <div className="form-group mb-4">
                                        <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Attachments</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            multiple
                                            onChange={handleFileUpload}
                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                            style={{ height: '42px', borderRadius: '5px', padding: '8px' }}
                                        />
                                        <small className="form-text text-muted" style={{ fontSize: '11px' }}>PDF, images, and documents</small>
                                    </div>

                                    <div className="modal-footer" style={{ borderTop: '1px solid #f0f0f0', padding: '20px 0 0 0', display: 'flex', gap: '10px' }}>
                                        <BSButton
                                            variant="light"
                                            style={{ height: '40px', borderRadius: '5px', fontWeight: '600', padding: '0 25px' }}
                                            onClick={() => {
                                                setShowAddRecord(false);
                                                resetNewRecord();
                                            }}
                                        >
                                            Cancel
                                        </BSButton>
                                        <BSButton
                                            type="submit"
                                            className="btn-primary"
                                            style={{ height: '40px', borderRadius: '5px', fontWeight: '600', padding: '0 25px', backgroundColor: 'var(--themecolor)', borderColor: 'var(--themecolor)' }}
                                        >
                                            Save Record
                                        </BSButton>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail View Modal */}
            {selectedRecord && (
                <div className="modal fade dc-uploadimages show" style={{
                    display: 'block',
                    paddingRight: '17px',
                    background: 'rgba(0,0,0,0.65)',
                    backdropFilter: 'blur(5px)',
                    zIndex: '9999'
                }} aria-modal="true" role="dialog">
                    <div className="dc-modaldialog modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: '620px' }}>
                        <div className="dc-modalcontent modal-content" style={{
                            borderRadius: '20px',
                            border: 'none',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden',
                            backgroundColor: '#fff'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                padding: '30px 40px 20px',
                                borderBottom: '1px solid #f1f5f9',
                                position: 'relative',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '16px',
                                    backgroundColor: '#eff6ff',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '15px'
                                }}>
                                    <MedicineBoxOutlined style={{ color: 'var(--themecolor)', fontSize: '28px' }} />
                                </div>
                                <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', margin: 0, letterSpacing: '-0.02em' }}>{selectedRecord.title}</h2>
                                <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '6px', fontWeight: '500' }}>Ref ID: #{selectedRecord.id || '--'}</p>

                                <button
                                    onClick={() => setSelectedRecord(null)}
                                    style={{
                                        position: 'absolute',
                                        right: '25px',
                                        top: '25px',
                                        border: 'none',
                                        background: '#f8fafc',
                                        borderRadius: '10px',
                                        width: '36px',
                                        height: '36px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#64748b',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#f8fafc'}
                                >
                                    <i className="fa fa-times" style={{ fontSize: '16px' }}></i>
                                </button>
                            </div>

                            {/* Scrollable Body Content */}
                            <div className="dc-modalbody modal-body" style={{
                                padding: '30px 40px',
                                maxHeight: '60vh',
                                overflowY: 'auto'
                            }}>
                                {/* Info Row */}
                                <div className="row g-4 mb-4">
                                    <div className="col-sm-6">
                                        <div style={{ backgroundColor: '#fff', border: '1px solid #f1f5f9', padding: '16px', borderRadius: '15px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '10px' }}>Classification</span>
                                            <div className="d-flex align-items-center gap-2">
                                                <span style={{ fontSize: '20px' }}>{getRecordTypeIcon(selectedRecord.record_type)}</span>
                                                <Badge bg="blue" style={{
                                                    backgroundColor: '#eff6ff',
                                                    color: '#1e40af',
                                                    fontWeight: '700',
                                                    fontSize: '13px',
                                                    padding: '4px 12px',
                                                    borderRadius: '8px',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {selectedRecord.record_type}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div style={{ backgroundColor: '#fff', border: '1px solid #f1f5f9', padding: '16px', borderRadius: '15px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '10px' }}>Consult Date</span>
                                            <div className="d-flex align-items-center gap-2" style={{ color: '#334155', fontWeight: '700', fontSize: '15px' }}>
                                                <CalendarOutlined style={{ color: '#94a3b8' }} />
                                                {formatDate(selectedRecord.record_date)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Provider Section */}
                                <div className="mb-4" style={{ backgroundColor: '#fcfdfe', border: '1px solid #f1f5f9', padding: '20px', borderRadius: '15px' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '15px' }}>Medical Facility & Provider</span>
                                    <div className="d-flex align-items-center gap-3">
                                        <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#fff', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <GlobalOutlined style={{ color: 'var(--themecolor)' }} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '800', color: '#1e293b', fontSize: '15px' }}>{selectedRecord.doctor_name || 'Medical Specialist'}</div>
                                            <div style={{ color: '#64748b', fontSize: '13px', fontWeight: '500' }}>{selectedRecord.hospital_name || 'Healthcare Center'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Notes Section */}
                                <div className="mb-4">
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '12px' }}>Clinical Summary & Notes</span>
                                    <div style={{
                                        fontSize: '14px',
                                        lineHeight: '1.8',
                                        color: '#475569',
                                        backgroundColor: '#fff',
                                        padding: '20px',
                                        borderRadius: '15px',
                                        border: '1px solid #f1f5f9',
                                        minHeight: '100px',
                                        fontWeight: '500'
                                    }}>
                                        {selectedRecord.diagnosis || selectedRecord.description || 'No detailed medical summary available.'}
                                    </div>
                                </div>

                                {/* Attachments Section */}
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '15px' }}>Associated Documents</span>
                                    <div className="d-flex flex-column gap-2">
                                        {(() => {
                                            let attachments = [];
                                            try {
                                                attachments = typeof selectedRecord.attachments === 'string'
                                                    ? JSON.parse(selectedRecord.attachments)
                                                    : selectedRecord.attachments;
                                            } catch (e) {
                                                attachments = [];
                                            }

                                            return Array.isArray(attachments) && attachments.length > 0 ? (
                                                attachments.map((attachment, index) => (
                                                    <div key={index} style={{
                                                        padding: '14px 18px',
                                                        borderRadius: '12px',
                                                        border: '1px solid #f1f5f9',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        backgroundColor: '#fff',
                                                        transition: 'all 0.2s',
                                                        cursor: 'pointer'
                                                    }}
                                                        onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--themecolor)'}
                                                        onMouseOut={(e) => e.currentTarget.style.borderColor = '#f1f5f9'}
                                                    >
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <FileTextOutlined style={{ color: 'var(--themecolor)' }} />
                                                            </div>
                                                            <div>
                                                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#334155', display: 'block' }}>{attachment}</span>
                                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Medical Report • Document</span>
                                                            </div>
                                                        </div>
                                                        <a href="javascript:;" style={{ color: 'var(--themecolor)' }} onClick={() => toast.info('Accessing file...')}>
                                                            <DownloadOutlined />
                                                        </a>
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{ padding: '20px', textAlign: 'center', borderRadius: '15px', border: '2px dashed #f1f5f9' }}>
                                                    <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>No clinical documents attached</span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="modal-footer" style={{
                                borderTop: '1px solid #f1f5f9',
                                padding: '20px 40px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px',
                                background: '#fff'
                            }}>
                                <BSButton
                                    onClick={() => setSelectedRecord(null)}
                                    style={{
                                        height: '46px',
                                        borderRadius: '12px',
                                        fontWeight: '700',
                                        padding: '0 25px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        color: '#64748b'
                                    }}
                                >
                                    Dismiss
                                </BSButton>
                                <BSButton
                                    className="btn-primary"
                                    onClick={() => toast.success('Syncing with printer...')}
                                    style={{
                                        height: '46px',
                                        borderRadius: '12px',
                                        fontWeight: '700',
                                        padding: '0 30px',
                                        backgroundColor: 'var(--themecolor)',
                                        borderColor: 'var(--themecolor)'
                                    }}
                                >
                                    Print Summary
                                </BSButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default UserMedicalRecords;
